import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { api } from '../services/api'
import type { Player } from '@territory-nexus/shared'

interface AuthState {
  player: Player | null
  token: string | null
  hasSession: boolean
  hasProfile: boolean
  isLoading: boolean
  error: string | null

  setAuth: (token: string | null, player: Player | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  refreshPlayer: () => Promise<void>
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      player: null,
      token: null,
      hasSession: false,
      hasProfile: false,
      isLoading: true,
      error: null,

      setAuth: (token, player) =>
        set({
          token,
          player,
          hasSession: !!token,
          hasProfile: !!player,
          isLoading: false,
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error, isLoading: false }),

      refreshPlayer: async () => {
        try {
          const response = await api.get<{ success: boolean; data: Player }>('/auth/profile')
          if (response.data.success && response.data.data) {
            set({ player: response.data.data })
          }
        } catch {
          // silently fail — player data stays stale until next refresh
        }
      },

      logout: () =>
        set({
          player: null,
          token: null,
          hasSession: false,
          hasProfile: false,
          error: null,
          isLoading: false,
        }),
    }),
    { name: 'AuthStore' }
  )
)
